import { useApi } from "/src/helpers/Hooks/useApi";
import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import { clsx } from "clsx";

const DayCell = ({ day, index, month, year }) => {
    // Calculate if this day is in the past or today
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let dayDate;
    let isPast = false;
    let isToday = false;

    if (!day.isPrevOrNextMonth && month && year) {
        // Parse month name to number (e.g., "January" -> 0)
        const monthNames = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];
        const monthIndex = monthNames.indexOf(month);

        dayDate = new Date(year, monthIndex, day.dayOfMonth);
        dayDate.setHours(0, 0, 0, 0);

        isPast = dayDate < today;
        isToday = dayDate.getTime() === today.getTime();
    }

    const isactualSelected = day.isSelected && index == 0;

    if (day.isPrevOrNextMonth) {
        return (
            <div
                className={clsx({
                    "flex flex-col items-center justify-center p-1 rounded-md w-full h-14 md:h-20 min-w-0": true,
                })}
            ></div>
        );
    }
    if (isactualSelected) {
        return (
            <div
                className={clsx({
                    "flex flex-col items-center justify-center p-1 rounded-md w-full h-14 md:h-20 min-w-0 bg-white text-primary border border-gray-200 ring-2 ring-emerald-300": true,
                })}
            >
                <span className="text-xs md:text-sm font-medium">{day.dayOfMonth}</span>
                {isToday && (
                    <span className="text-[8px] md:text-[10px] font-semibold text-primary">
                        Today
                    </span>
                )}
                {day.hasAvailability && (
                    <div className="w-full flex flex-col items-center mt-1">
                        <span className="text-md font-semibold text-emp">£{day.price}</span>
                        {day.isCheapest && (
                            <div className="w-full bg-background text-green-600 font-semibold text-center py-1 rounded-md">
                                Cheapest
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // If day is in the past, is today, or has no availability, render as non-clickable and greyed out
    if (isPast || isToday || !day.hasAvailability) {
        return (
            <div
                className={clsx(
                    {
                        "flex flex-col items-center justify-center p-1 rounded-md w-full h-14 md:h-20 min-w-0": true,
                    },
                    { "bg-gray-100 text-gray-400 border border-gray-200 opacity-50": true }
                )}
            >
                <span className="text-xs md:text-sm font-medium">{day.dayOfMonth}</span>
                {isToday && (
                    <span className="text-[8px] md:text-[10px] font-semibold text-gray-500">
                        Today
                    </span>
                )}
            </div>
        );
    }

    const cellContent = (
        <div
            className={clsx(
                {
                    "flex flex-col items-center lg:p-1 md:p-1 p-0 rounded-md w-full h-14 md:h-20 min-w-0": true,
                },
                { "": day.isPrevOrNextMonth },
                { "bg-white text-primary border border-gray-200": !day.isPrevOrNextMonth },
                { "ring-2 ring-emerald-300": isactualSelected },
                {
                    "cursor-pointer hover:bg-gray-50":
                        day.hasAvailability && !isactualSelected && day.link,
                },
                { "justify-center": !day.isCheapest && !isToday },
                { "justify-end": day.isCheapest }
            )}
        >
            <span className="text-xs md:text-sm font-medium">{day.dayOfMonth}</span>
            {isToday && (
                <span className="text-[8px] md:text-[10px] font-semibold text-primary">Today</span>
            )}
            {day.hasAvailability && (
                <div className="w-full flex flex-col items-center">
                    <span className="lg:text-[14px] md:text-[10px] text-[8px] font-semibold text-emp">
                        £{day.price}
                    </span>
                    {day.isCheapest && (
                        <div class="w-full">
                            <div class="lg:block md:block hidden w-full bg-background text-green-600 font-semibold text-center py-1 px-1 rounded-md lg:text-[14px] md:text-[10px] text-[8px] truncate">
                                Cheapest
                            </div>
                            <div class="lg:hidden md:hidden block w-full bg-background text-green-600 font-semibold text-center py-1 px-1 rounded-md text-[8px] truncate">
                                Chp.
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    // If there's a link, wrap in Link component, otherwise just return the content
    if (day.link) {
        return <Link to={day.link}>{cellContent}</Link>;
    }

    return cellContent;
};
// Shared by the package and hotel-only result cards - `product` picks the API
// (hotel-only has no departure airports and prices re-margined server-side).
export default function PackageSearchResultCalendar({ result, onClose, product = "package" }) {
    const [currentData, setCurrentData] = useState({});
    const [currentOffset, setCurrentOffset] = useState(0);
    const api = useApi();
    useEffect(() => {
        async function fetchData() {
            const rooms = result.rooms.map((x) => x.roomRef).join();
            const data = product == "hotelOnly"
                ? await api.getHotelOnlyCalendar(
                    result.hotel.id,
                    result.source,
                    result.startDate,
                    currentOffset,
                    rooms,
                    result.duration
                )
                : await api.getPropertyCalendar(
                    result.hotel.id,
                    result.searchedDepartures,
                    result.startDate,
                    currentOffset,
                    rooms,
                    result.duration
                );
            const newData = { ...currentData };
            newData[currentOffset] = data;
            setCurrentData(newData);
        }
        if (!currentData[currentOffset]) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch calendar data when the viewed month (offset) changes
    }, [currentOffset]);
    const prevMonth = async () => {
        const newOffset = currentOffset - 1;
        setCurrentOffset(newOffset);
    };

    const nextMonth = async () => {
        const newOffset = currentOffset + 1;
        setCurrentOffset(newOffset);
    };

    // Check if we can go back (prevent going to past months)
    const canGoPrevious = () => {
        if (!selectedData) return false;

        // Calculate what the previous month would be
        const monthNames = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];
        const currentMonthIndex = monthNames.indexOf(selectedData.month);

        let prevMonthIndex = currentMonthIndex - 1;
        let prevYear = selectedData.year;

        if (prevMonthIndex < 0) {
            prevMonthIndex = 11; // December
            prevYear = selectedData.year - 1;
        }

        // Get today's month and year
        const today = new Date();
        const todayMonthIndex = today.getMonth();
        const todayYear = today.getFullYear();

        // Allow going back if the previous month is >= current month
        if (prevYear > todayYear) return true;
        if (prevYear === todayYear && prevMonthIndex >= todayMonthIndex) return true;

        return false;
    };

    const selectedData = currentData[currentOffset];
    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="transition flex items-center justify-center min-h-screen lg:pt-14 md:pt-12 pt-6 px-4 pb-20 text-center sm:block sm:p-0">
                <div
                    className="fixed inset-0 bg-gray-500/75 bg-opacity-75"
                    onClick={() => onClose()}
                ></div>
                <div className="inline-block transform translate-0 transition-allalign-bottom bg-white rounded-2xl pt-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full lg:max-w-[75vw] md:max-w-[90vw] sm:max-w-[95vw]">
                    <div className="absolute top-2 right-2 z-20">
                        <button
                            onClick={() => onClose()}
                            className="text-gray-400 hover:text-content_3"
                        >
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>

                    <div className="p-4">
                        <div
                            id="calendar-module-modal"
                            className="bg-white rounded-[18px] lg:p-3 md:p-2 pt-2 pb-2 p-0 mb-0"
                        >
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <button
                                        onClick={() => prevMonth()}
                                        disabled={!canGoPrevious()}
                                        className={clsx(
                                            "p-2 rounded-full text-white",
                                            canGoPrevious()
                                                ? "bg-primary hover:bg-secondary cursor-pointer"
                                                : "bg-gray-300 cursor-not-allowed opacity-50"
                                        )}
                                    >
                                        <svg
                                            className="w-5 h-5 text-content_2"
                                            fill="none"
                                            stroke="white"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M15 19l-7-7 7-7"
                                            ></path>
                                        </svg>
                                    </button>
                                    <h3 className="text-base md:text-lg font-semibold text-primary">
                                        {selectedData?.month} {selectedData?.year}
                                    </h3>
                                    <button
                                        onClick={() => nextMonth()}
                                        className="p-2 rounded-full bg-primary hover:bg-secondary text-white"
                                    >
                                        <svg
                                            className="w-5 h-5 text-content_2"
                                            fill="none"
                                            stroke="white"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M9 5l7 7-7 7"
                                            ></path>
                                        </svg>
                                    </button>
                                </div>

                                <div className="grid grid-cols-7 text-center text-xs md:text-sm font-medium text-content_2 mb-2">
                                    <div className="py-1">Mon</div>
                                    <div className="py-1">Tue</div>
                                    <div className="py-1">Wed</div>
                                    <div className="py-1">Thu</div>
                                    <div className="py-1">Fri</div>
                                    <div className="py-1">Sat</div>
                                    <div className="py-1">Sun</div>
                                </div>

                                <div className="grid grid-cols-7 lg:gap-2 gap-1 max-h-[60vh] md:max-h-[65vh] overflow-y-auto lg:px-1 md:px-1 px-[0px]">
                                    {currentData[currentOffset] &&
                                        currentData[currentOffset].days.map((day, index) => (
                                            <DayCell
                                                key={index}
                                                index={currentOffset}
                                                day={day}
                                                month={selectedData?.month}
                                                year={selectedData?.year}
                                            />
                                        ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="w-full flex flex-col items-center pt-2 pb-2 mt-2 bg-error_2 text-error">
                        Price displayed are Per Person.
                    </div>
                </div>
            </div>
        </div>
    );
}
